import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';
import { compact } from 'lodash';
import { Stack, Button } from 'react-bootstrap';

import { AmountWithDiscount } from '@components/Base';
import { PolicyCommissionInvoiceList } from '@models/PolicyCommissionInvoice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import { formatDateTime } from '@utils/datetime';

import { selectFeaturePolicyCommissionInvoice } from './selector';
import {
  policyCommissionInvoicesFetch,
  PolicyCommissionInvoicesFetchPayload,
  stateClear,
} from './action';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

type Props = {
  id: number;
  onShow?: (invoice: PolicyCommissionInvoiceList) => void;
};

const PolicyCommissionInvoiceListView = ({
  id: insurancePolicyId,
  onShow,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { invoices, isLoading, isReload, pagination } = useSelector(selectFeaturePolicyCommissionInvoice);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const canManage = !!onShow;

  const columns = useMemo(() => compact([
    canManage && {
      cell: (invoice) => (
        <Stack direction="horizontal" gap={1}>
          {!!onShow && (
            <Button onClick={() => onShow(invoice)} variant="outline-info" size="sm">
              <FontAwesomeIcon icon={faEye} />
            </Button>
          )}
        </Stack>
      ),
      button: true,
      center: true,
    },
    {
      name: t('common:createdDate'),
      selector: ({ createdAt }) => formatDateTime(createdAt),
      minWidth: '180px',
    },
    {
      name: "Cash flow",
      selector: ({ cashFlow }) => cashFlow,
      minWidth: '120px',
    },
    {
      name: "Payment status",
      selector: ({ paymentStatus }) => paymentStatus,
      minWidth: '140px',
    },
    {
      name: "Total premium",
      selector: ({ totalPremium }) => <AmountWithDiscount finalAmount={totalPremium} skipIncluded />,
      width: '140px',
    },
    {
      name: "Insurer amount",
      selector: ({ insurerCommissionAmount }) => <AmountWithDiscount finalAmount={insurerCommissionAmount} skipIncluded />,
      width: '140px',
    },
    {
      name: "MGA amount",
      selector: ({ mgaCommissionAmount }) => <AmountWithDiscount finalAmount={mgaCommissionAmount} skipIncluded />,
      width: '140px',
    },
    {
      name: "Total frontline amount",
      selector: ({ totalFrontlineCommissionAmount }) => <AmountWithDiscount finalAmount={totalFrontlineCommissionAmount} skipIncluded />,
      width: '280px',
    },
  ]) as TableColumn<PolicyCommissionInvoiceList>[], []);

  useEffect(() => {
    const payload: PolicyCommissionInvoicesFetchPayload = { insurancePolicyId, page, perPage };
    dispatch(policyCommissionInvoicesFetch(payload));
  }, [insurancePolicyId, page, perPage]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  useUpdateEffect(() => {
    const payload: PolicyCommissionInvoicesFetchPayload = { insurancePolicyId, page: 1, perPage };
    dispatch(policyCommissionInvoicesFetch(payload));
  }, [isReload]);

  return (
    <DataTable
      columns={columns}
      data={invoices ?? []}
      customStyles={customStyles}
      fixedHeader
      pagination
      paginationServer
      progressPending={isLoading}
      paginationPerPage={perPage}
      paginationTotalRows={pagination?.total ?? 0}
      onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
      onChangePage={(newPage) => setPage(newPage)}
    />
  );
};

export { PolicyCommissionInvoiceListView };
